import { useState } from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'
import { Button } from './ui'
import {
  canDecreaseTextScale,
  canIncreaseTextScale,
  decreaseTextScale,
  getTextScale,
  getThemePreference,
  increaseTextScale,
  setTextScale,
  setThemePreference,
  type LrTextScale,
  type LrThemePreference
} from '../lib/lrUiPreferences'

const THEME_OPTIONS: {
  value: LrThemePreference
  Icon: typeof Sun
  label: string
}[] = [
  { value: 'light', Icon: Sun, label: 'Tagmodus (hell)' },
  { value: 'system', Icon: Monitor, label: 'Automatisch (System)' },
  { value: 'dark', Icon: Moon, label: 'Nachtmodus (dunkel)' }
]

function applyTextScale(next: LrTextScale, setTextScaleState: (scale: LrTextScale) => void) {
  setTextScale(next)
  setTextScaleState(next)
}

export default function HeaderDisplayControls() {
  const [theme, setTheme] = useState<LrThemePreference>(() => getThemePreference())
  const [textScale, setTextScaleState] = useState<LrTextScale>(() => getTextScale())
  const canDecrease = canDecreaseTextScale(textScale)
  const canIncrease = canIncreaseTextScale(textScale)

  return (
    <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5 sm:gap-x-3">
      <div
        className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50/80 p-0.5 dark:border-gray-600 dark:bg-gray-800/60"
        role="group"
        aria-label="Farbschema"
      >
        {THEME_OPTIONS.map(({ value, Icon, label }) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={theme === value ? 'primary' : 'ghost'}
            className="h-8 w-8 shrink-0 px-0"
            title={label}
            aria-label={label}
            aria-pressed={theme === value}
            onClick={() => {
              setThemePreference(value)
              setTheme(value)
            }}
          >
            <Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
          </Button>
        ))}
      </div>

      <div
        className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50/80 p-0.5 dark:border-gray-600 dark:bg-gray-800/60"
        role="group"
        aria-label="Schriftgröße"
      >
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 min-w-[2rem] shrink-0 px-2 font-semibold leading-none text-gray-700 dark:text-gray-200"
          title="Schrift verkleinern"
          aria-label="Schrift verkleinern"
          disabled={!canDecrease}
          onClick={() => applyTextScale(decreaseTextScale(textScale), setTextScaleState)}
        >
          <span className="text-[11px]" aria-hidden>
            A
          </span>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 min-w-[2rem] shrink-0 px-2 font-semibold leading-none text-gray-700 dark:text-gray-200"
          title="Schrift vergrößern"
          aria-label="Schrift vergrößern"
          disabled={!canIncrease}
          onClick={() => applyTextScale(increaseTextScale(textScale), setTextScaleState)}
        >
          <span className="text-lg" aria-hidden>
            A
          </span>
        </Button>
      </div>
    </div>
  )
}
